import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
/**
 * /launcher/link — shows a one-time code the user pastes into the launcher.
 *
 * A frontend route rather than a standalone page so the standard LogInModal,
 * sign-up and password reset all behave exactly as they do elsewhere on the
 * site. Nothing is ever postMessage'd: an earlier design broadcast the token
 * with targetOrigin "*", which hands a credential to whatever page framed this
 * one — and the site deliberately sends SameSite=None cookies so it CAN be
 * framed. The user carries a code across by hand instead.
 */
export default class LinkPage extends Page {
  username = '';
  linked = false;
  loading = false;
  failed = false;
  oninit(vnode) {
    super.oninit(vnode);
    if (app.session.user) {
      this.approve();
    }
  }

  /**
   * 🚨 The login dialog is opened here, OUT of the render cycle — never from
   * oninit.
   *
   * ModalManagerState.show() performs a synchronous redraw. Calling it during
   * oninit happens while Mithril is rendering this very node, so Mithril throws
   * "Node is currently being rendered to and thus is locked". The modal still
   * appears, because it mounts in its own root — but THIS page's tree is left
   * broken, every later redraw throws "t.tag is not a function", and the
   * "Linked as ..." state can never render. A dead page behind a working
   * dialog is the worst possible shape, since nothing looks wrong.
   *
   * setTimeout(0) defers it to after the current render completes.
   */
  oncreate(vnode) {
    super.oncreate(vnode);
    if (!app.session.user) {
      setTimeout(() => this.promptLogin(), 0);
    }
  }
  onupdate() {
    // The modal resolves nothing on dismissal and signing in redraws the app,
    // so the session is re-read on redraw rather than awaited. This also covers
    // signing up or resetting a password inside the modal.
    if (app.session.user && !this.linked && !this.loading && !this.failed) {
      this.approve();
    }
  }

  /**
   * Flarum's modals resolve nothing on dismissal, so there is no "logged in or
   * not" promise to await. The page re-reads the session when the modal closes
   * instead, which also covers signing up or resetting a password inside it.
   */
  /**
   * 🚨 A LAZY import, not a static one. Flarum 2 code-splits LogInModal
   * (`addChunkModule("226","1793","core","forum/components/LogInModal")`), so a
   * static import resolves to undefined and `flarum.reg.get()` returns
   * undefined too — get() only suppresses the warning for chunk modules, it
   * does not load them. `app.modal.show(undefined)` then throws and no dialog
   * appears at all, which is exactly the bug this page shipped with.
   *
   * ModalManager.show() accepts a function returning a promise, which is what
   * core itself compiles to. flarum-webpack-config rewrites this dynamic import
   * to flarum.reg.asyncModuleImport(), so it does NOT emit a chunk of our own —
   * which matters, because Flarum publishes only the named entry bundles and an
   * extra chunk would 404.
   *
   * `remember: true` pre-ticks Remember me, which is what issues the
   * flarum_remember cookie. Without it the session is short, and that is why
   * the launcher kept finding itself logged out.
   */
  promptLogin() {
    app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal'), {
      remember: true
    });
  }

  /**
   * Approves whatever pairing the launcher planted in the bz_pair cookie.
   *
   * The body is EMPTY on purpose — the endpoint reads the cookie and ignores
   * any pair_id sent here. A page that could nominate the pairing would be a
   * page an attacker could aim, which is the hole the earlier `?pair=` design
   * had. The cookie is HttpOnly, so this code cannot see it either.
   */
  approve() {
    this.loading = true;
    this.failed = false;
    app.request({
      method: 'POST',
      url: `${app.forum.attribute('apiUrl')}/launcher/link/approve`,
      body: {},
      // 🚨 Returning false SUPPRESSES Flarum's own error alert. Catching in
      // .catch() is not enough: app.request shows the alert itself before it
      // rejects, which is why a perfectly successful link still flashed
      // "Oops! Something went wrong" for a couple of seconds. Anything other
      // than false here falls back to the default handler, which is what we
      // still want for a genuine failure.
      errorHandler: error => {
        if (error?.status === 409 || error?.status === 410) {
          this.loading = false;
          this.linked = true;
          this.username = app.session.user?.displayName() ?? '';
          m.redraw();
          return false;
        }
      }
    }).then(result => {
      this.loading = false;
      // 204 means there was no pairing to approve — somebody is just looking
      // at the page outside the launcher. Not a failure.
      this.linked = !!(result && result.status === 'approved');
      this.username = result && result.username || (app.session.user?.displayName() ?? '');
      m.redraw();
    }).catch(error => {
      // 409 (already approved) and 410 (already collected) are the ORDINARY
      // path, not an edge case: the server-side LoggedIn listener approves
      // first and the launcher consumes the pairing within two seconds, so
      // by the time this call lands the work is usually done. Already handled
      // in errorHandler above; this only has to avoid marking it failed.
      if (error?.status === 409 || error?.status === 410) {
        return;
      }
      this.loading = false;
      this.failed = true;
      m.redraw();
    });
  }
  view() {
    return m("div", {
      className: "LauncherLinkPage"
    }, m("div", {
      className: "container"
    }, m("div", {
      className: "LauncherLinkPage-card"
    }, m("h1", null, app.translator.trans('blizznux-launcher.forum.link.title')), this.content())));
  }
  content() {
    if (!app.session.user) {
      return [m("p", {
        className: "LauncherLinkPage-intro"
      }, app.translator.trans('blizznux-launcher.forum.link.intro')), m("p", null, m(Button, {
        className: "Button Button--primary",
        onclick: () => this.promptLogin()
      }, app.translator.trans('blizznux-launcher.forum.link.log_in')))];
    }
    if (this.loading) {
      return m(LoadingIndicator, null);
    }
    if (this.failed) {
      return [m("p", {
        className: "LauncherLinkPage-intro"
      }, app.translator.trans('blizznux-launcher.forum.link.failed')), m("p", null, m(Button, {
        className: "Button Button--primary",
        onclick: () => this.approve()
      }, app.translator.trans('blizznux-launcher.forum.link.retry')))];
    }
    if (this.linked) {
      return [m("p", {
        className: "LauncherLinkPage-linked"
      }, app.translator.trans('blizznux-launcher.forum.link.linked', {
        username: m("strong", null, this.username)
      })), m("p", {
        className: "LauncherLinkPage-small"
      }, app.translator.trans('blizznux-launcher.forum.link.linked_hint'))];
    }

    // Signed in, nothing to approve: somebody opened the page outside the
    // launcher. Say so plainly rather than showing a spinner forever.
    return [m("p", {
      className: "LauncherLinkPage-signedIn"
    }, app.translator.trans('blizznux-launcher.forum.link.signed_in_as', {
      username: m("strong", null, app.session.user?.displayName())
    })), m("p", {
      className: "LauncherLinkPage-small"
    }, app.translator.trans('blizznux-launcher.forum.link.no_pairing'))];
  }
}
flarum.reg.add('blizznux-launcher', 'forum/components/LinkPage', LinkPage);